import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useTheme } from "@/contexts/ThemeContext";
import { THEME_BACKGROUNDS, TRANSITION_VIDEOS } from "@/constants/theme";

/**
 * ThemeTransition (global)
 * ------------------------
 * Mounted once at the app root. Two responsibilities:
 *  1. Preload the transition videos + both hero background images so the
 *     cinematic transition is instant whenever it's triggered.
 *  2. Render the fast crossfade overlay used for the non-cinematic path
 *     (other pages, scrolled away from the hero, reduced-motion, or the user
 *     disabled cinematic transitions). The actual hero video lives in
 *     <HeroThemeVideo /> so it can be positioned exactly over the hero background.
 */

// Solid base color used to briefly mask the instant swap during a crossfade.
const BASE_COLOR: Record<"dark" | "light", string> = {
  dark: "#050505",
  light: "#FAFAFA",
};

export default function ThemeTransition() {
  const { transition, endTransition } = useTheme();

  // Preload assets once.
  useEffect(() => {
    const urls = Object.values(TRANSITION_VIDEOS).flatMap((v) => [v.webm, v.mp4]);
    const videos = urls.map((src) => {
      const v = document.createElement("video");
      v.preload = "auto";
      v.muted = true;
      v.src = src;
      v.load();
      return v;
    });
    Object.values(THEME_BACKGROUNDS).forEach((src) => {
      const img = new Image();
      img.src = src;
    });
    return () => {
      videos.forEach((v) => {
        v.removeAttribute("src");
        v.load();
      });
    };
  }, []);

  const isCrossfade = transition?.mode === "crossfade";

  return (
    <AnimatePresence>
      {isCrossfade && (
        <motion.div
          key="theme-transition-crossfade"
          className="fixed inset-0 z-[9998] pointer-events-none"
          style={{ backgroundColor: transition.to ? BASE_COLOR.dark : BASE_COLOR.light }}
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 0.9, 0] }}
          transition={{ duration: 0.45, times: [0, 0.4, 1], ease: "easeInOut" }}
          onAnimationComplete={() => endTransition()}
          data-testid="theme-transition-crossfade"
          aria-hidden="true"
        />
      )}
    </AnimatePresence>
  );
}
